import type { ComponentType, ReactNode } from 'react';
import { Button, InputGroup, Label, Popover, TextField } from '@heroui/react';
import { Segment } from '@heroui-pro/react';
import { IconCheck, IconLink, IconLinkOff } from '@tabler/icons-react';
import {
  AI_IMAGE_ASPECT_RATIO_OPTIONS,
  AI_IMAGE_RESOLUTION_LABELS,
} from '../constants';
import type { AiImageAspectRatioId, AiImageResolution, AiImageSettings } from '../types';
import {
  getBaseSize,
  scaleHeightFromWidth,
  scaleWidthFromHeight,
} from '../utils/image-size';

type SegmentItemComponentProps = {
  id: string;
  className?: string;
  children?: ReactNode;
};

/** heroui-pro Segment.Item 类型未完整暴露 id/children，运行时均支持 */
const SegmentItem = Segment.Item as ComponentType<SegmentItemComponentProps>;

const AI_IMAGE_ASPECT_RATIO_ROWS = [
  AI_IMAGE_ASPECT_RATIO_OPTIONS.slice(0, 4),
  AI_IMAGE_ASPECT_RATIO_OPTIONS.slice(4),
] as const;

export type AiImageSettingsPopoverProps = {
  settings: AiImageSettings;
  onChange: (next: AiImageSettings) => void;
  summary: string;
  disabled?: boolean;
};

export function AiImageSettingsPopover({
  settings,
  onChange,
  summary,
  disabled,
}: AiImageSettingsPopoverProps) {
  const applyAspectRatio = (aspectRatio: AiImageAspectRatioId) => {
    const size = getBaseSize(aspectRatio, settings.resolution);
    onChange({ ...settings, aspectRatio, width: size.width, height: size.height });
  };

  const applyResolution = (resolution: AiImageResolution) => {
    const size = getBaseSize(settings.aspectRatio, resolution);
    onChange({ ...settings, resolution, width: size.width, height: size.height });
  };

  return (
    <Popover>
      <Popover.Trigger>
        <Button
          className="h-8 rounded-full border px-3 text-sm"
          isDisabled={disabled}
          size="sm"
          variant="secondary"
        >
          {summary}
        </Button>
      </Popover.Trigger>
      <Popover.Content className="w-[min(420px,calc(100vw-2rem))] p-4">
        <div className="flex flex-col gap-5">
          <div>
            <Label className="text-muted mb-2 block text-xs">选择比例</Label>
            <div className="flex flex-col gap-2">
              {AI_IMAGE_ASPECT_RATIO_ROWS.map((row) => (
                <Segment
                  key={row.map((item) => item.id).join('-')}
                  className="w-full"
                  isDisabled={disabled}
                  selectedKey={settings.aspectRatio}
                  size="sm"
                  variant="ghost"
                  onSelectionChange={(key) => {
                    const id = String(key) as AiImageAspectRatioId;
                    if (AI_IMAGE_ASPECT_RATIO_OPTIONS.some((o) => o.id === id)) {
                      applyAspectRatio(id);
                    }
                  }}
                >
                  {row.map((item) => (
                    <SegmentItem key={item.id} id={item.id}>
                      <Segment.Separator />
                      {item.label}
                    </SegmentItem>
                  ))}
                </Segment>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-muted mb-2 block text-xs">选择分辨率</Label>
            <Segment
              className="w-full"
              isDisabled={disabled}
              selectedKey={settings.resolution}
              size="sm"
              onSelectionChange={(key) => {
                const resolution = String(key) as AiImageResolution;
                if (resolution === '2k' || resolution === '4k') {
                  applyResolution(resolution);
                }
              }}
            >
              <SegmentItem id="2k">
                <Segment.Separator />
                {AI_IMAGE_RESOLUTION_LABELS['2k']}
              </SegmentItem>
              <SegmentItem id="4k">
                <Segment.Separator />
                {AI_IMAGE_RESOLUTION_LABELS['4k']}
              </SegmentItem>
            </Segment>
          </div>

          <div>
            <Label className="text-muted mb-2 block text-xs">尺寸</Label>
            <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2">
              <TextField aria-label="宽度" className="min-w-0" fullWidth>
                <InputGroup fullWidth variant="secondary">
                  <InputGroup.Prefix className="shrink-0">W</InputGroup.Prefix>
                  <InputGroup.Input
                    className="min-w-0"
                    inputMode="numeric"
                    disabled={disabled}
                    value={String(settings.width)}
                    onChange={(e) => {
                      const width = Number(e.target.value.replace(/\D/g, '')) || 0;
                      onChange({
                        ...settings,
                        width,
                        height: settings.linked
                          ? scaleHeightFromWidth(width, settings.aspectRatio)
                          : settings.height,
                      });
                    }}
                  />
                  <InputGroup.Suffix className="shrink-0">PX</InputGroup.Suffix>
                </InputGroup>
              </TextField>
              <Button
                isIconOnly
                aria-label={settings.linked ? '解除比例锁定' : '锁定比例'}
                isDisabled={disabled}
                size="sm"
                variant="ghost"
                onPress={() => onChange({ ...settings, linked: !settings.linked })}
              >
                {settings.linked ? (
                  <IconLink className="size-4" stroke={1.75} />
                ) : (
                  <IconLinkOff className="size-4" stroke={1.75} />
                )}
              </Button>
              <TextField aria-label="高度" className="min-w-0" fullWidth>
                <InputGroup fullWidth variant="secondary">
                  <InputGroup.Prefix className="shrink-0">H</InputGroup.Prefix>
                  <InputGroup.Input
                    className="min-w-0"
                    inputMode="numeric"
                    disabled={disabled}
                    value={String(settings.height)}
                    onChange={(e) => {
                      const height = Number(e.target.value.replace(/\D/g, '')) || 0;
                      onChange({
                        ...settings,
                        height,
                        width: settings.linked
                          ? scaleWidthFromHeight(height, settings.aspectRatio)
                          : settings.width,
                      });
                    }}
                  />
                  <InputGroup.Suffix className="shrink-0">PX</InputGroup.Suffix>
                </InputGroup>
              </TextField>
            </div>
          </div>
        </div>
      </Popover.Content>
    </Popover>
  );
}

export function ModelMenuItem({
  label,
  selected,
}: {
  label: string;
  selected?: boolean;
}) {
  return (
    <span className="flex w-full items-center justify-between gap-2">
      <span className="truncate">{label}</span>
      {selected ? <IconCheck className="size-4 shrink-0" stroke={2} /> : null}
    </span>
  );
}
